import {ThreeTsl as TSL} from "#material/three/exports"
import type {Three as THREE} from "#material/three/exports"
import {dFdxFloat, dFdyFloat} from "#material/three/gradient"

/**
 * UV-space gradient via UV Jacobian inversion from a pre-evaluated height float.
 * Returns vec2(dh/du, dh/dv).
 *
 * dFdx(h) / dFdy(h) are in screen space. WebGL (window Y up) and WebGPU (framebuffer
 * Y down) disagree on the sign of dFdy, so dFdy(h) has the opposite sign to dh/dv in
 * WebGL. Inverting the 2×2 UV Jacobian cancels that flip consistently in both backends.
 */
const threeUvGradient = TSL.Fn(([h]: [h: THREE.Node<"float">]) => {
    const dh_dx = dFdxFloat(h)
    const dh_dy = dFdyFloat(h)

    const duv_dx = TSL.dFdx(TSL.uv(0)) // [du/dx_screen, dv/dx_screen]
    const duv_dy = TSL.dFdy(TSL.uv(0)) // [du/dy_screen, dv/dy_screen]

    const det = duv_dx.x.mul(duv_dy.y).sub(duv_dx.y.mul(duv_dy.x))
    const dh_du = dh_dx.mul(duv_dy.y).sub(dh_dy.mul(duv_dx.y)).div(det)
    const dh_dv = dh_dy.mul(duv_dx.x).sub(dh_dx.mul(duv_dy.x)).div(det)

    return TSL.vec2(dh_du, dh_dv)
})

// Common encode step: UV-space gradient → tangent-space normal map colour (0–1 RGB).
// sc converts UV-space gradient to world-space: UV-per-pixel / world-per-pixel.
export const threeEncodeNormal = TSL.Fn(([gradUV, scale, uv]: [gradUV: THREE.Node<"vec2">, scale: THREE.Node<"float">, uv: THREE.Node<"vec2">]) => {
    const dpt_dx = TSL.vec3(dFdxFloat(TSL.positionView.x), dFdxFloat(TSL.positionView.y), dFdxFloat(TSL.positionView.z))
    const sc = TSL.length(TSL.dFdx(uv)).div(TSL.length(dpt_dx))
    const D = gradUV.mul(scale).mul(sc)
    return TSL.normalize(TSL.vec3(D.x.negate(), D.y.negate(), 1.0))
        .mul(0.5)
        .add(0.5)
})

// Procedural / virtual path: UV Jacobian gradient → encode.
export const threeHeightToNormal = TSL.Fn(([h, scale]: [h: THREE.Node<"float">, scale: THREE.Node<"float">]) => {
    return threeEncodeNormal(threeUvGradient(h), scale, TSL.uv(0))
})
